import React, { useState } from 'react';
import { Bell, Heart, MessageCircle, UserPlus, Briefcase, Calendar, MoreHorizontal } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Separator } from '../components/ui/separator';
import { mockNotifications } from '../data/mock';

const Notifications = () => {
  const [notifications, setNotifications] = useState(mockNotifications);

  const markAsRead = (id) => {
    setNotifications(prev =>
      prev.map(notif =>
        notif.id === id ? { ...notif, unread: false } : notif
      )
    );
  };

  const markAllAsRead = () => {
    setNotifications(prev =>
      prev.map(notif => ({ ...notif, unread: false }))
    );
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'like':
        return <Heart className="w-5 h-5 text-red-500" />;
      case 'comment':
        return <MessageCircle className="w-5 h-5 text-blue-500" />;
      case 'connection':
        return <UserPlus className="w-5 h-5 text-green-500" />;
      case 'job':
        return <Briefcase className="w-5 h-5 text-purple-500" />;
      case 'event':
        return <Calendar className="w-5 h-5 text-orange-500" />;
      default:
        return <Bell className="w-5 h-5 text-gray-500" />;
    }
  };

  const extendedNotifications = [
    ...notifications,
    {
      id: "4",
      type: "job",
      message: "New training job posted: Senior L&D Specialist at Microsoft",
      time: "2 days ago",
      unread: false
    },
    {
      id: "5",
      type: "event",
      message: "Reminder: Future of Training Conference starts tomorrow",
      time: "3 days ago",
      unread: false
    },
    {
      id: "6",
      type: "like",
      message: "Your article on virtual training got 50+ likes",
      time: "1 week ago",
      unread: false
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Bell className="w-6 h-6 mr-3 text-blue-600" />
              <CardTitle className="text-lg">Notifications</CardTitle>
              <Badge className="ml-3 bg-blue-600">
                {notifications.filter(n => n.unread).length}
              </Badge>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={markAllAsRead}
              disabled={!notifications.some(n => n.unread)}
            >
              Mark all as read
            </Button>
          </div>
          <p className="text-sm text-gray-600">
            Stay updated with your professional network activity
          </p>
        </CardHeader>
      </Card>

      <Tabs defaultValue="all" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="all">
            All ({extendedNotifications.length})
          </TabsTrigger>
          <TabsTrigger value="unread">
            Unread ({extendedNotifications.filter(n => n.unread).length})
          </TabsTrigger>
          <TabsTrigger value="mentions">
            Mentions (0)
          </TabsTrigger>
          <TabsTrigger value="jobs">
            Jobs (1)
          </TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="space-y-4">
          {extendedNotifications.map((notification) => (
            <Card
              key={notification.id}
              className={`hover:shadow-md transition-shadow cursor-pointer ${
                notification.unread ? 'bg-blue-50 border-blue-200' : ''
              }`}
              onClick={() => markAsRead(notification.id)}
            >
              <CardContent className="p-4">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    {getNotificationIcon(notification.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm ${notification.unread ? 'font-semibold' : 'text-gray-700'}`}>
                      {notification.message}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">{notification.time}</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    {notification.unread && (
                      <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                    )}
                    <Button variant="ghost" size="sm">
                      <MoreHorizontal className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="unread" className="space-y-4">
          {extendedNotifications.filter(n => n.unread).length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <Bell className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="font-semibold text-lg mb-2">All caught up!</h3>
                <p className="text-gray-600">You have no unread notifications.</p>
              </CardContent>
            </Card>
          ) : (
            extendedNotifications.filter(n => n.unread).map((notification) => (
              <Card
                key={notification.id}
                className="bg-blue-50 border-blue-200 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => markAsRead(notification.id)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold">{notification.message}</p>
                      <p className="text-xs text-gray-500 mt-1">{notification.time}</p>
                    </div>
                    <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>

        <TabsContent value="mentions" className="space-y-4">
          <Card>
            <CardContent className="p-8 text-center">
              <MessageCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="font-semibold text-lg mb-2">No mentions yet</h3>
              <p className="text-gray-600">When someone mentions you in a post or comment, it will appear here.</p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="jobs" className="space-y-4">
          <Card className="hover:shadow-md transition-shadow cursor-pointer">
            <CardContent className="p-4">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <Briefcase className="w-5 h-5 text-purple-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-700">
                    New training job posted: Senior L&D Specialist at Microsoft
                  </p>
                  <p className="text-xs text-gray-500 mt-1">2 days ago</p>
                  <div className="mt-2">
                    <Button size="sm" variant="outline">
                      View Job
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Notifications;